import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CuttysarkBallot,
  CuttysarkCandidate,
} from '@cortex/domain-product-innovation-lab-types';

/** @graph-connects none */
interface BallotDetailsProps {
  ballots: CuttysarkBallot[];
  candidates: CuttysarkCandidate[];
}

/**
 * 運営向けの投票明細（誰が誰に入れたか・採点）。運営以外には API が返さない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞セッションの投票明細（投票者ごとの投票先・採点・送信日時）を運営に表示する
 * @graph-connects none
 */
export function BallotDetails({ ballots, candidates }: BallotDetailsProps): ReactNode {
  const { t } = useTranslation();
  const nameOf = new Map(candidates.map((c) => [c.email, c.name]));
  const name = (email: string): string => nameOf.get(email) ?? email;

  if (ballots.length === 0) {
    return (
      <p className="text-sm text-[var(--color-text-muted)]">{t('cuttysark.operate.noBallots')}</p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead className="text-xs text-[var(--color-text-muted)]">
          <tr>
            <th className="px-2 py-1">{t('cuttysark.operate.voter')}</th>
            <th className="px-2 py-1">{t('cuttysark.operate.votes')}</th>
            <th className="px-2 py-1">{t('cuttysark.operate.scores')}</th>
            <th className="px-2 py-1">{t('cuttysark.operate.submittedAt')}</th>
          </tr>
        </thead>
        <tbody>
          {ballots.map((ballot) => (
            <tr key={ballot.voterEmail} className="border-t border-[var(--color-border)] align-top">
              <td className="px-2 py-1 text-[var(--color-text-primary)]">{ballot.voterEmail}</td>
              <td className="px-2 py-1 text-[var(--color-text-secondary)]">
                {ballot.votes
                  .flatMap((v) => v.candidateEmails)
                  .map(name)
                  .join(', ')}
              </td>
              <td className="px-2 py-1 text-[var(--color-text-secondary)]">
                {ballot.scores
                  .map(
                    (s) =>
                      `${name(s.candidateEmail)} ${String(s.achievement ?? '-')}/${String(s.future ?? '-')}`,
                  )
                  .join(', ')}
              </td>
              <td className="whitespace-nowrap px-2 py-1 text-[var(--color-text-muted)]">
                {new Date(ballot.submittedAt).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
